import { Injectable, TemplateRef } from '@angular/core';

import { ButtonsType } from '@nrwl/rx-models';
import { ComponentType } from '@angular/cdk/portal';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';

type DialogPayload = {
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  maxHeight?: string | number;
  panelClass?: string;
  disableClose?: boolean;
  backdropClass?: string;
  position?: any;
  hasBackdrop?: boolean;
  type?: any;
  [key: string]: any;
};
type DialogResult<T> = T | undefined;

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  buttonsType = ButtonsType;
  tabletWidth = '768px';
  constructor(
    private dialog: MatDialog, // private store: Store<State>
  ) {}

  public onCustomClick$ = new Subject<any>();
  public pageObservable = new Subject<number>();
  public sizeObservable = new Subject<number>();
  pageConfig(i: number) {
    this.pageObservable.next(i);
  }
  sizeConfig(i: number) {
    this.sizeObservable.next(i);
  }

  public open<TComponent, TData extends DialogPayload, TResult = any>(
    dialogComponent: ComponentType<TComponent> | TemplateRef<TComponent>,
    data: TData,
  ): MatDialogRef<TComponent, TResult> {
    // by the default disable possibility to close dialog on outside click
    const disableOutsideClickClose = data.disableClose !== false;
    const width = data.width != null ? String(data.width) : '600px';
    const minWidth = data.minWidth != null ? String(data.minWidth) : width;
    const maxWidth = data.maxWidth != null ? String(data.maxWidth) : undefined;
    const maxHeight = data.maxHeight != null ? String(data.maxHeight) : undefined;

    return this.dialog.open(dialogComponent, {
      width,
      minWidth,
      ...(maxWidth ? { maxWidth } : {}),
      ...(maxHeight ? { maxHeight } : {}),
      panelClass: data.panelClass ? data.panelClass : '',
      disableClose: disableOutsideClickClose,
      backdropClass: data.backdropClass,
      data: data,
      position: data.position,
      hasBackdrop: data.hasBackdrop ?? true,
    });
  }

  public close() {
    this.dialog.closeAll();
  }

  public show<TComponent, TData extends DialogPayload, TResult = any>(
    dialogComponent: ComponentType<TComponent> | TemplateRef<TComponent>,
    data: TData,
  ): Observable<DialogResult<TResult>> {
    return this.open<TComponent, TData, TResult>(dialogComponent, data).afterClosed();
  }

  public modal<TComponent, TData extends DialogPayload, TResult = any>(
    dialogComponent: ComponentType<TComponent> | TemplateRef<TComponent>,
    data: TData,
  ): MatDialogRef<TComponent, TResult> {
    return this.dialog.open(dialogComponent, {
      data: data,
    });
  }

  public saveDialog<TComponent, TData extends DialogPayload, TResult = any>(
    dialogComponent: ComponentType<TComponent> | TemplateRef<TComponent>,
    data: TData,
  ): Observable<DialogResult<TResult>> {
    data.type = this.buttonsType.save;
    return this.open<TComponent, TData, TResult>(dialogComponent, data).afterClosed();
  }
  public confirmDialog<TComponent, TData extends DialogPayload, TResult = any>(
    dialogComponent: ComponentType<TComponent> | TemplateRef<TComponent>,
    data: TData,
  ): Observable<DialogResult<TResult>> {
    data.type = this.buttonsType.confirm;
    return this.open<TComponent, TData, TResult>(dialogComponent, data).afterClosed();
  }

  public infoDialog<TComponent, TData extends DialogPayload, TResult = any>(
    dialogComponent: ComponentType<TComponent> | TemplateRef<TComponent>,
    data: TData,
  ): Observable<DialogResult<TResult>> {
    data.type = this.buttonsType.info;
    return this.open<TComponent, TData, TResult>(dialogComponent, data).afterClosed();
  }

  public pdfDialog<TComponent, TData extends DialogPayload, TResult = any>(
    dialogComponent: ComponentType<TComponent> | TemplateRef<TComponent>,
    data: TData,
  ): Observable<DialogResult<TResult>> {
    data.type = this.buttonsType.info;
    data.width = this.tabletWidth;
    return this.open<TComponent, TData, TResult>(dialogComponent, data).afterClosed();
  }

  public prompt<TComponent, TData extends DialogPayload, TResult = any>(
    dialogComponent: ComponentType<TComponent> | TemplateRef<TComponent>,
    data: TData,
  ): Observable<DialogResult<TResult>> {
    return this.open<TComponent, TData, TResult>(dialogComponent, data).afterClosed();
  }
}
